/**
 * Nav/index.js
 * desc: primary navigation (a.k.a. vertical nav)
 * note: stateless presentational component
 */
'use strict';

import { useState } from 'react';
import { NavLink } from 'react-router';
import { AiModelsIcon, AnalyticsIcon, BookIcon, ChevronLeftIcon, ChevronRightIcon, ComponentsIcon, HomeIcon, DiscoverIcon, JourneyIcon, ReviewsIcon, SettingsIcon, TestIcon, DatabaseIcon } from '../Icon';
import { classNames } from '../../utilities';
import { getItem, setItem } from '../../utilities/local-storage';
import { trackEvent } from '../../utilities/analytics';
import { translate } from '../../utilities/localization';
import { hasAccess } from '../../utilities/authentication';
import appUrls from '../../settings/app-urls';
import WikiPagesDialog from '../../modules/WikiPages';
import { WIKI_PAGES_PERMISSIONS } from '../../modules/WikiPages/constants';
import './styles.css';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../store/selectors/users';
import { useFlags } from 'launchdarkly-react-client-sdk';

const Nav = () => {
    //*************************************************
    //*****   Data   **********************************
    //*************************************************
    // State
    const [ collapsed, setCollapsed ] = useState(getItem('navCollapsed') || false);
    const [ isWikiPagesOpen, setIsWikiPagesOpen ] = useState(false);
    // Store state
    const user = useSelector(selectCurrentUser);
    // Variables
    const clientNamespace = window.location.hostname.split('.')[0];
    const userPermissions = user?.permissions || [];
    // Feature flags
    const featureFlags = useFlags();
    const hasDiscoverFeature = featureFlags['web.discover.feature.clientList']?.includes(clientNamespace);
    // no route behind this entry, so the nav is the only client-side gate
    const hasWikiPagesFeature = hasAccess(WIKI_PAGES_PERMISSIONS, userPermissions);


    //*************************************************
    //*****   Public Functions   **********************
    //*************************************************
    const handleAiModelsLinkClick = () => trackEvent('Global: Nav - AI Models Link (Click)');

    const handleAnalyticsLinkClick = () => trackEvent('Global: Nav - Analytics Link (Click)');

    const handleComponentsLinkClick = () => trackEvent('Global: Nav - Components Link (Click)');

    const handleHomeLinkClick = () => trackEvent('Global: Nav - Home Link (Click)');

    const handleDiscoverLinkClick = () => trackEvent('Global: Nav - Discover Link (Click)');

    const handleJourneysLinkClick = () => trackEvent('Global: Nav - Journeys Link (Click)');

    const handleReviewsLinkClick = () => trackEvent('Global: Nav - Reviews Link (Click)');

    const handleSettingsLinkClick = () => trackEvent('Global: Nav - Settings Link (Click)');

    const handleTestingLinkClick = () => trackEvent('Global: Nav - Testing Link (Click)');

    const handleCustomDataClick = () => trackEvent('Global: Nav - Cutom Data (Click)');

    const handleWikiPagesClick = () => {
        trackEvent('Global: Nav - Wiki Pages Link (Click)');
        setIsWikiPagesOpen(true);
    };

    const handleToggleClick = () => {
        // track event
        const toggleText = collapsed ? 'Expand' : 'Collapse';
        trackEvent(`Global: Nav - ${toggleText} Icon (Click)`);

        // update state and persist in localStorage
        setCollapsed(!collapsed);
        setItem('navCollapsed', !collapsed);
    };


    //*************************************************
    //*****   Private Functions   *********************
    //*************************************************
    const _getClassList = () => classNames({
        collapsed: collapsed,
    });


    //*************************************************
    //*****   Render   ********************************
    //*************************************************
    return (
        <nav id="nav" className={ _getClassList() }>
            <menu>
                <NavLink
                    to={ appUrls.HOME } 
                    className={ ({ isActive }) =>
                        isActive ? 'selected' : undefined
                    }
                    onClick={ handleHomeLinkClick }
                >
                    <HomeIcon readOnly={ true }/>
                    <figcaption>{ translate('nav.link.home') }</figcaption>
                </NavLink>

                { hasAccess([ 'feature-ai:view' ], userPermissions) ?
                    <NavLink to={ appUrls.AI } className={ ({ isActive }) =>
                        isActive ? 'selected' : undefined
                    } onClick={ handleAiModelsLinkClick }>
                        <AiModelsIcon readOnly={ true }/>
                        <figcaption>{ translate('nav.link.ai') }</figcaption>
                    </NavLink>
                    :
                    null
                }

                { hasAccess([ 'feature-analytics:view' ], userPermissions) ?
                    <NavLink to={ appUrls.ANALYTICS } className={ ({ isActive }) =>
                        isActive ? 'selected' : undefined
                    } onClick={ handleAnalyticsLinkClick }>
                        <AnalyticsIcon readOnly={ true }/>
                        <figcaption>{ translate('nav.link.analytics') }</figcaption>
                    </NavLink>
                    :
                    null
                }

                { hasAccess([ 'feature-components:view' ], userPermissions) ?
                    <NavLink to={ appUrls.COMPONENTS } className={ ({ isActive }) =>
                        isActive ? 'selected' : undefined
                    } onClick={ handleComponentsLinkClick }>
                        <ComponentsIcon readOnly={ true }/>
                        <figcaption>{ translate('nav.link.components') }</figcaption>
                    </NavLink>
                    :
                    null
                }

                { hasAccess([ 'feature-custom-data:view' ], userPermissions) ?
                    <NavLink to={ appUrls.CUSTOM_DATA } className={ ({ isActive }) =>
                        isActive ? 'selected' : undefined
                    } onClick={ handleCustomDataClick }>
                        <DatabaseIcon readOnly={ true }/>
                        <figcaption>{ translate('nav.link.customData') }</figcaption>
                    </NavLink>
                    :
                    null
                }

                { hasAccess([ 'feature-journeys:view' ], userPermissions) ?
                    <NavLink to={ appUrls.JOURNEYS } className={ ({ isActive }) =>
                        isActive ? 'selected' : undefined
                    } onClick={ handleJourneysLinkClick }>
                        <JourneyIcon readOnly={ true }/>
                        <figcaption>{ translate('nav.link.journeys') }</figcaption>
                    </NavLink>
                    :
                    null
                }

                { hasAccess([ 'feature-reviews:view' ], userPermissions) ?
                    <NavLink to={ appUrls.REVIEWS } className={ ({ isActive }) =>
                        isActive ? 'selected' : undefined
                    } onClick={ handleReviewsLinkClick }>
                        <ReviewsIcon readOnly={ true }/>
                        <figcaption>{ translate('nav.link.reviews') }</figcaption>
                    </NavLink>
                    :
                    null
                }

                { featureFlags['web.testing.feature.enabled'] && hasAccess([ 'feature-testing:view' ], userPermissions) ?
                    <NavLink to={ appUrls.TESTING } className={ ({ isActive }) =>
                        isActive ? 'selected' : undefined
                    } onClick={ handleTestingLinkClick }>
                        <TestIcon readOnly={ true }/>
                        <figcaption>{ translate('nav.link.testing') }</figcaption>
                    </NavLink>
                    :
                    null
                }
            </menu>

            { collapsed ?
                <ChevronRightIcon className="toggle" onClick={ handleToggleClick } tooltip={ translate('icons.flyoutExpand.tooltip.text') }/>
                :
                <ChevronLeftIcon className="toggle" onClick={ handleToggleClick } tooltip={ translate('icons.flyoutCollapse.tooltip.text') }/>
            }

            <menu>
                { hasDiscoverFeature && hasAccess([ 'feature-discover:view' ], userPermissions) ?
                    <NavLink to={ appUrls.DISCOVER } className={ ({ isActive }) =>
                        isActive ? 'selected' : undefined
                    } onClick={ handleDiscoverLinkClick }>
                        <DiscoverIcon readOnly={ true }/>
                        <figcaption>{ translate('nav.link.discover') }</figcaption>
                    </NavLink>
                    :
                    null
                }

                { hasWikiPagesFeature ?
                    <button type="button" className="wiki-pages" onClick={ handleWikiPagesClick }>
                        <BookIcon readOnly={ true } tooltip={ translate('nav.link.wikiPages') }/>
                        <figcaption>{ translate('nav.link.wikiPages') }</figcaption>
                    </button>
                    :
                    null
                }

                { hasAccess([ 'feature-settings:view' ], userPermissions) ?
                    <NavLink to={ appUrls.SETTINGS } className={ ({ isActive }) =>
                        isActive ? 'selected' : undefined
                    } onClick={ handleSettingsLinkClick }>
                        <SettingsIcon readOnly={ true }/>
                        <figcaption>{ translate('nav.link.settings') }</figcaption>
                    </NavLink>
                    :
                    null
                }
            </menu>

            { hasWikiPagesFeature ?
                <WikiPagesDialog open={ isWikiPagesOpen } onOpenChange={ setIsWikiPagesOpen }/>
                :
                null
            }
        </nav>
    );
};


export default Nav;
